'use client';
import { LineItem } from '@repo/ui/src/types/lineItem';
import React, { useState } from 'react';
import Quantity from '@components/Cart/_Quantity';
import { classNames } from '@repo/ui/src/utils/classNames';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import ProductPricing from './_ProductPricing';
import Image from '@repo/ui/src/components/Image';
import { useDebounceCallback } from '@hooks/useDebouncedCallback';
import useUpdateBasket from '@hooks/basket/useUpdateBasket';
import LoadingLogo from '@components/Loading/LoadingLogo';

export type UpdateType = 'stepper' | 'remove';

export type HandleChange = (newQuantity: number) => void;

interface SingleLineItemProps {
  lineItem: LineItem;
  outOfStock?: boolean;
}

const SingleLineItem = ({
  lineItem,
  outOfStock,
}: SingleLineItemProps) => {
  const { trigger: updateBasket, isMutating: isLoadingUpdate } = useUpdateBasket();
  const [updateType, setUpdateType] = useState<UpdateType>();
  const { t } = useTranslation();
  const handleChange = useDebounceCallback(
    (newQuantity: number, type: UpdateType) => {
      updateBasket({ lineItem, quantity: newQuantity });
      setUpdateType(type);
    },
    [lineItem],
    500,
  );

  if (!lineItem) return null;

  return (
    <li
      className="tw-border-b tw-border-warm-200 tw-grid tw-gap-y-8 tw-py-6"
      data-testid="product-line-item"
    >
      <div className="tw-flex tw-flex-col" role="group" aria-label="product">
        {outOfStock && (
          <div className="tw-bg-red-400 tw-px-4 tw-py-2 tw-mb-6">
            <p className="tra-text-web-body-m tw-text-white">
              {t('cart.outOfStockText', undefined, {
                default:
                  'This item is out of stock. Remove from your cart to complete your purchase.',
              })}
            </p>
          </div>
        )}
        <div className="tw-w-full tw-grid tw-gap-x-6 tw-grid-cols-[20%_3fr_1fr]">
          <div className={classNames('tw-relative', outOfStock && 'tw-opacity-50')}>
            <Image
              src={lineItem?.getMainImageSize(96, 1, 1)}
              alt=""
              layout="responsive"
              width={96}
              height={96}
            />
          </div>

          <div className={classNames('tw-w-full', outOfStock && 'tw-opacity-50')}>
            <div>
              <h5 className="tw-text-left tw-font-bold tra-text-web-body-l">
                {lineItem?.title}
              </h5>
              <div className="tw-flex tw-flex-col">
                <Quantity
                  editable={true}
                  lineItem={lineItem}
                  isLoading={isLoadingUpdate}
                  handleChange={handleChange}
                  outOfStock={outOfStock}
                />
              </div>
            </div>
          </div>

          <div className="tw-flex tw-flex-col tw-items-end tw-justify-between sm:tw-justify-start">
            <div className="tw-h-6">
              {isLoadingUpdate && updateType == 'stepper' ? (
                <LoadingLogo />
              ) : (
                !outOfStock && <ProductPricing item={lineItem} editable={true} />
              )}
            </div>

            {!lineItem.bonus &&
              (isLoadingUpdate && updateType === 'remove' ? (
                <LoadingLogo />
              ) : (
                <>
                  {outOfStock && (
                    <p className="tra-text-web-body-l tw-overflow-hidden tw-whitespace-nowrap tw-bold tw-opacity-50">
                      {t('productDetailPageContent.outOfStockText', undefined, {
                        default: 'Out of Stock',
                      })}
                    </p>
                  )}
                  <button
                    className={classNames(
                      'tw-underline tra-text-web-body-m',
                      outOfStock ? 'tw-mt-0' : 'tw-mt-7 ',
                    )}
                    data-testid="c-remove-btn"
                    onClick={() => handleChange(0, 'remove')}
                  >
                    {t('cart.cartRemoveText', undefined, { default: 'Remove' })}
                  </button>
                </>
              ))}
          </div>
        </div>
      </div>
    </li>
  );
};

export default SingleLineItem;
